import { CalendarClock, CheckCircle2, Clock } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { IPaymentPlan } from '@/lib/builder/models/payment-plan.model';
import {
    hasMultipleInstallments,
    hasPaymentInFlight,
    summarizePlan,
} from '@/lib/builder/utils/payment-plan.utils';
import { formatPlanDateShort } from '@/lib/builder/utils/payment-format';

interface TravelerPaymentSummaryProps {
    plan: IPaymentPlan | null | undefined;
    /** true quando l'itinerario e' gia' saldato per intero. */
    isSettled: boolean;
}

/**
 * Una riga sotto il prezzo che dice al cliente a che punto e' il pagamento, senza
 * aprire nulla: totale unico, oppure quante rate ha pagato e quando scade la prossima.
 *
 * Serve perche' il prezzo da solo e' ambiguo: "€ 4.100" non distingue "devi pagarli
 * adesso" da "ne hai gia' versati 1.500 e il resto scade a settembre". Il dettaglio
 * completo sta nella modale, qui c'e' solo l'informazione che serve a decidere se
 * aprirla.
 */
export const TravelerPaymentSummary = ({ plan, isSettled }: TravelerPaymentSummaryProps) => {
    const { t, language } = useI18n();

    const summary = summarizePlan(plan);
    const hasInstallments = hasMultipleInstallments(plan);
    const paymentInFlight = hasPaymentInFlight(plan);

    // Prima di tutto il resto: un pagamento in volo e' l'unica cosa che il cliente vuole
    // sapere quando torna a guardare, e senza questa riga la proposta gli direbbe "da
    // saldare" per tutti i giorni in cui il bonifico e' in viaggio.
    if (paymentInFlight && !hasInstallments) {
        return (
            <div className="flex items-center justify-end gap-1 mt-0.5 text-blue-700">
                <Clock className="h-3.5 w-3.5" />
                <span className="text-sm">{t('itinerary.payment.summary.processing')}</span>
            </div>
        );
    }

    // Col piano rate comanda il piano: lo status 'completed' arriva gia' al primo acconto.
    if (summary ? summary.allPaid : isSettled) {
        return (
            <div className="flex items-center justify-end gap-1 mt-0.5 text-green-700">
                <CheckCircle2 className="h-3.5 w-3.5" />
                <span className="text-sm">{t('itinerary.payment.summary.allPaid')}</span>
            </div>
        );
    }

    // Nessun piano, o piano in soluzione unica: non c'e' nulla da contare.
    if (!hasInstallments || !summary) {
        return (
            <div className="flex items-center justify-end gap-1 mt-0.5">
                <span className="text-sm text-gray-500">{t('itinerary.payment.summary.single')}</span>
            </div>
        );
    }

    const nextDate = summary.nextDue ? formatPlanDateShort(summary.nextDue.due_date, language) : null;

    return (
        <div className="flex flex-wrap items-center justify-end gap-x-1.5 gap-y-0.5 mt-0.5">
            <span className="text-sm text-gray-500">
                {/* `total` e non `count`: i18next tratta `count` come selettore di plurale
                    e cercherebbe chiavi con suffisso che qui non esistono. */}
                {t('itinerary.payment.summary.installments', {
                    paid: summary.paidCount,
                    total: summary.totalCount,
                })}
            </span>
            {/* La rata di turno ha un addebito in volo: la sua scadenza non e' piu'
                l'informazione utile, lo e' il fatto che il cliente ha gia' pagato. */}
            {summary.nextDue?.payment_processing ? (
                <span className="flex items-center gap-1 text-sm text-blue-700">
                    <Clock className="h-3.5 w-3.5" />
                    {t('itinerary.payment.summary.processing')}
                </span>
            ) : (
                nextDate && (
                    <span
                        className={`flex items-center gap-1 text-sm ${summary.isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}
                    >
                        <CalendarClock className="h-3.5 w-3.5" />
                        {summary.isOverdue
                            ? t('itinerary.payment.summary.overdueSince', { date: nextDate })
                            : t('itinerary.payment.summary.nextDue', { date: nextDate })}
                    </span>
                )
            )}
        </div>
    );
};
